import type { PropType, VNode } from 'vue';
import type { BlockDefinition, BlockSerialized, BlockSerializedChild } from '#layers/block-engine/types/block';
import type { BlockProgrammaticChildAdapter, BlockProgrammaticChildRegistration } from '#layers/block-engine/utils/block/api/programmatic-child';
import { computed, defineComponent, h, provide, useId, watchEffect } from 'vue';
import { noop } from '#layers/base/public';
import { useBlockEditorChildBounds } from '#layers/block-engine/composables/block-editor/use-block-editor-child-bounds';
import { useBlockEditorContext } from '#layers/block-engine/composables/use-block-editor-context';
import { blockChildKey, useBlockChild, useBlockInEditorContext } from '#layers/block-engine/utils/block/api/context';
import { useBlockInEditor } from '#layers/block-engine/utils/block/api/in-editor';
import { blockProgrammaticChildAdapterKey } from '#layers/block-engine/utils/block/api/programmatic-child';
import BlockRender from './BlockRender';

interface BlockProgrammaticChildProps {
	block: string | BlockSerialized | BlockDefinition;
	childId?: string;
	props?: Record<string, any>;
	propMap?: Record<string, string>;
	slotMap?: Record<string, string>;
	skipInParentChain?: boolean;
}

export default defineComponent({
	name: 'BlockProgrammaticChild',

	props: {
		block: {
			type: [String, Object] as PropType<BlockProgrammaticChildProps['block']>,
			required: true,
		},
		childId: {
			type: String,
			default: undefined,
		},
		props: {
			type: Object as PropType<Record<string, any>>,
			default: undefined,
		},
		propMap: {
			type: Object as PropType<Record<string, string>>,
			default: undefined,
		},
		slotMap: {
			type: Object as PropType<Record<string, string>>,
			default: undefined,
		},
		skipInParentChain: {
			type: Boolean,
			default: false,
		},
	},

	setup(props, { attrs, slots }) {
		const getParentChild = useBlockChild();
		const parentEditorContext = useBlockInEditorContext();
		const blockInEditor = useBlockInEditor();
		const blockEditorContext = blockInEditor ? useBlockEditorContext() : null;

		const blockId = computed(() => typeof props.block === 'string' ? props.block : props.block.id);
		const parentChild = computed(() => getParentChild());
		const programmaticInstanceId = useId();
		const syntheticChildId = computed(() => props.childId ?? `${parentChild.value.id}::programmatic:${blockId.value}:${programmaticInstanceId}`);

		function getMappedParentPropKey(prop: string) {
			return props.propMap?.[prop] ?? prop;
		}

		function getMappedParentSlotName(slot: string) {
			return props.slotMap?.[slot] ?? slot;
		}

		function getMappedChildSlotName(slot: string) {
			return Object.entries(props.slotMap ?? {}).find(([, parentSlotName]) => parentSlotName === slot)?.[0] ?? slot;
		}

		const mappedBlockProps = computed(() => {
			const baseProps = {
				...props.props,
			};

			for (const [propName, parentPropName] of Object.entries(props.propMap ?? {})) {
				// Resolved runtime props may differ from their serialized expressions.
				if (!Object.hasOwn(baseProps, propName)) {
					baseProps[propName] = parentChild.value.props[parentPropName];
				}
			}

			return baseProps;
		});

		const syntheticChild = computed<BlockSerializedChild>(() => {
			const slotNames = new Set<string>([
				...Object.keys(props.slotMap ?? {}),
				...Object.keys(parentChild.value.slots ?? {}),
			]);

			return {
				id: syntheticChildId.value,
				blockId: blockId.value,
				props: mappedBlockProps.value,
				slots: [...slotNames].reduce((acc, slotName) => {
					acc[slotName] = parentChild.value.slots[getMappedParentSlotName(slotName)] ?? [];
					return acc;
				}, {} as Record<string, string[]>),
			};
		});

		const { onBlockRef: updateBoundsBlockRef } = blockInEditor
			? useBlockEditorChildBounds(() => ({ childId: syntheticChildId.value }))
			: { onBlockRef: noop };

		if (blockInEditor) {
			const adapter: BlockProgrammaticChildAdapter = {
				getChild: () => syntheticChild.value,
				slotIsEmpty: (slot = 'default') => parentEditorContext.slotIsEmpty(getMappedParentSlotName(slot)),
				getSlot: (slot) => parentEditorContext.getSlot(getMappedParentSlotName(slot)),
				getSlotNames: () => Object.keys(syntheticChild.value.slots),
				removeSlotChildren: (slot) => parentEditorContext.removeSlotChildren(getMappedParentSlotName(slot)),
				onBlockTreeSlotClick: (callback) => parentEditorContext.onBlockTreeSlotClick((payload) => {
					callback({
						slotName: getMappedChildSlotName(payload.slotName),
					});
				}),
				getProps: () => mappedBlockProps.value,
				setProp: (prop, value) => {
					parentEditorContext.setProp(getMappedParentPropKey(prop as string), value);
				},
				insertBlock: (options) => {
					const slot = getMappedParentSlotName(options.target?.slot ?? 'default');

					return parentEditorContext.insertBlock({
						...options,
						target: {
							...options.target,
							slot,
						},
					});
				},
				moveBlock: (options) => {
					const slot = getMappedParentSlotName(options.target?.slot ?? 'default');

					parentEditorContext.moveBlock({
						...options,
						target: {
							...options.target,
							slot,
						},
					});
				},
				isPreview: parentEditorContext.isPreview,
				triggerLayoutUpdate: parentEditorContext.triggerLayoutUpdate,
			};

			provide(blockProgrammaticChildAdapterKey, adapter);
			provide(blockChildKey, () => syntheticChild.value);

			watchEffect((onCleanup) => {
				if (!blockEditorContext?.programmaticChildren) {
					return;
				}

				const registrationId = syntheticChild.value.id;
				const registration: BlockProgrammaticChildRegistration = {
					child: syntheticChild.value,
					parentChildId: parentChild.value.id,
					slotMap: props.slotMap,
					skipInParentChain: props.skipInParentChain,
					hiddenInBlockTree: true,
					selectable: false,
					draggable: false,
					inspectable: false,
					showSelectionUi: false,
				};

				blockEditorContext.programmaticChildren.value.set(registrationId, registration);

				onCleanup(() => {
					blockEditorContext.programmaticChildren?.value.delete(registrationId);
				});
			});
		}

		function getWrappedSlots() {
			const wrappedSlots: Record<string, (...args: any[]) => VNode[]> = {};

			for (const [slotName, slotFn] of Object.entries(slots)) {
				const wrappedSlotName = getMappedChildSlotName(slotName);

				wrappedSlots[wrappedSlotName] = (...args: any[]) => slotFn?.(...args) ?? [];
			}

			return wrappedSlots;
		}

		function onBlockRef(value: any) {
			updateBoundsBlockRef(value);

			const forwarded = (attrs as Record<string, any>)['onBlock:ref'];

			if (typeof forwarded === 'function') {
				forwarded(value);
			}
		}

		return () => h(BlockRender, {
			...attrs,
			'onBlock:ref': onBlockRef,
			'block': props.block,
			'props': mappedBlockProps.value,
			'unwrapBlockDefinition': true,
		}, getWrappedSlots());
	},
});
